<template>
  <div>
    <v-dialog
      v-model="dialogVisibleDirty"
      scrollable
      max-width="600px"
      content-class="fill-height"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      :transition="$vuetify.breakpoint.xsOnly ? 'dialog-bottom-transition' : 'scale-transition'"
      :persistent="$store.state.ui.alert.active"
    >
      <v-card class="fill-height">
        <!--<editor-fold desc="Dialog title">-->
        <v-card-title
          class="grey lighten-3"
          :class="{ 'pa-2': $vuetify.breakpoint.xsOnly, 'pa-4': $vuetify.breakpoint.smAndUp }"
        >
          <div class="flex-fill">
            <v-row
              no-gutters
              :class="{ 'mb-1': $vuetify.breakpoint.xsOnly, 'mb-3': !$vuetify.breakpoint.xsOnly }"
              align="center"
            >
              <v-col
                class="title"
              >
                <slot name="dialogTitle">
                  {{ dialogTitle }}
                </slot>
              </v-col>
              <v-col
                cols="auto"
                class="hidden-sm-and-up"
              >
                <v-btn
                  icon
                  text
                  class="ma-0"
                  @click="dialogVisibleDirty = false"
                >
                  <v-icon>close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <div>
              <v-text-field
                v-model="inventoryItems.search"
                solo
                placeholder="Search by label or code..."
                append-icon="search"
                hide-details
                clearable
                :loading="inventoryItems.loading"
                @input="fetchInventoryItems()"
              />
            </div>
            <div
              v-if="selectedInventoryItems.length > 0"
            >
              <validation-observer ref="validationObserver">
                <v-row
                  v-for="item in selectedInventoryItems"
                  :key="'selected_'+item.id"
                  no-gutters
                  class="py-2 pl-6 list--bordered"
                  align="center"
                >
                  <v-col>
                    <div
                      class="text-body-1"
                    >
                      {{ item.label }}
                    </div>
                    <div class="grey--text caption">
                      {{ item.code }}
                    </div>
                  </v-col>
                  <v-col cols="auto">
                    <validation-provider
                      v-slot="{ errors }"
                      rules="required|min_value:0"
                    >
                      <v-text-field
                        v-model="item.quantity"
                        :error-messages="errors"
                        type="number"
                        min="0"
                        outlined
                        hide-details
                        dense
                        background-color="white"
                        single-line
                        :suffix="item.unit"
                        style="width: 130px"
                      />
                    </validation-provider>
                  </v-col>
                  <v-col
                    cols="auto"
                    class="px-3"
                  >
                    <v-icon
                      color="error"
                      @click="removeInventoryItem(item)"
                    >
                      close
                    </v-icon>
                  </v-col>
                </v-row>
              </validation-observer>
            </div>
          </div>
        </v-card-title>
        <!--</editor-fold>-->
        <v-divider />
        <!--<editor-fold desc="Dialog content">-->
        <v-card-text class="pa-0 fill-height">
          <div
            v-if="inventoryItems.data.length === 0 && !inventoryItems.search"
            class="pa-6 text-center"
          >
            Search inventory
          </div>
          <div
            v-else-if="inventoryItems.data.length === 0"
            class="pa-6 text-center"
          >
            No parts found
          </div>
          <v-virtual-scroll
            ref="virtualScroll"
            item-height="58px"
            :items="inventoryItems.data"
          >
            <template
              v-slot:default="{ index, item }"
            >
              <v-row
                align="center"
                no-gutters
                class="list--bordered py-2 px-6 clickable"
                @click="addInventoryItem(item)"
              >
                <v-col>
                  <div>
                    {{ item.label }}
                  </div>
                  <div class="grey--text caption">
                    {{ item.code }}
                  </div>
                </v-col>
              </v-row>
            </template>
          </v-virtual-scroll>
        </v-card-text>
        <!--</editor-fold>-->
        <v-divider />
        <!--<editor-fold desc="Dialog actions">-->
        <v-card-actions
          class="pa-4 shrink"
        >
          <v-spacer />
          <v-btn
            color="grey"
            text
            @click="dialogVisibleDirty = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="submit()"
          >
            <v-icon left>
              check
            </v-icon>
            Submit
          </v-btn>
        </v-card-actions>
        <!--</editor-fold>-->
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="customPartDialogVisibleDirty"
      scrollable
      max-width="600px"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      :transition="$vuetify.breakpoint.xsOnly ? 'dialog-bottom-transition' : 'scale-transition'"
      :persistent="$store.state.ui.alert.active"
    >
      <v-card>
        <!--<editor-fold desc="Dialog title">-->
        <v-card-title
          class="grey lighten-3"
          :class="{ 'pa-2': $vuetify.breakpoint.xsOnly, 'pa-4': $vuetify.breakpoint.smAndUp }"
        >
          <div class="flex-fill">
            <v-row
              no-gutters
              align="center"
            >
              <v-col
                class="title"
              >
                <slot name="dialogTitle">
                  {{ customPart.index !== undefined ? 'Edit custom part' : 'Add custom part' }}
                </slot>
              </v-col>
              <v-col
                cols="auto"
                class="hidden-sm-and-up"
              >
                <v-btn
                  icon
                  text
                  class="ma-0"
                  @click="customPartDialogVisibleDirty = false"
                >
                  <v-icon>close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-card-title>
        <!--</editor-fold>-->
        <!--<editor-fold desc="Dialog content">-->
        <v-card-text class="pa-6">
          <validation-observer ref="customPartValidationObserver">
            <validation-provider
              v-slot="{ errors }"
              rules="required"
              name="Label"
            >
              <v-text-field
                v-model="customPart.label"
                outlined
                label="Part Label"
                :error-messages="errors"
                autocomplete="disabled"
                autofocus
              />
            </validation-provider>

            <v-row no-gutters>
              <v-col
                cols="8"
                class="pr-2"
              >
                <validation-provider
                  v-slot="{ errors }"
                  name="Code"
                >
                  <v-text-field
                    v-model="customPart.code"
                    outlined
                    label="Code"
                    :error-messages="errors"
                    hint="Barcode or another unique identification"
                    autocomplete="disabled"
                  />
                </validation-provider>
              </v-col>
              <v-col
                cols="8"
                class="pl-2"
              >
                <validation-provider
                  v-slot="{ errors }"
                  rules="required"
                  name="Unit"
                >
                  <v-text-field
                    v-model="customPart.unit"
                    outlined
                    label="Measuring Unit"
                    :error-messages="errors"
                    autocomplete="disabled"
                    placeholder="Enter unit"
                    hint="e.g. pcs / kg / cm / m3 / ..."
                  />
                </validation-provider>
              </v-col>
            </v-row>

            <v-row no-gutters>
              <v-col
                cols="11"
                class="pr-2"
              >
                <validation-provider
                  v-slot="{ errors }"
                  rules="required"
                  name="Price"
                >
                  <currency-input
                    v-model="customPart.unit_price"
                    name="unit_price"
                    :error-messages="errors"
                    label="Unit Price"
                    placeholder="Enter price per 1 unit"
                    autocomplete="disabled"
                    outlined
                  />
                </validation-provider>
              </v-col>
              <v-col
                cols="5"
                class="pl-2"
              >
                <validation-provider
                  v-slot="{ errors }"
                  rules="required"
                  name="Currency"
                >
                  <v-select
                    v-model="customPart.currency"
                    label="Currency"
                    :error-messages="errors"
                    outlined
                    single-line
                    :items="currencies"
                  />
                </validation-provider>
              </v-col>
            </v-row>

            <validation-provider
              v-slot="{ errors }"
              rules="required"
              name="Quantity"
            >
              <v-text-field
                v-model="customPart.quantity"
                type="number"
                :error-messages="errors"
                label="Quantity"
                autocomplete="disabled"
                outlined
                style="width: 150px"
              />
            </validation-provider>
          </validation-observer>
        </v-card-text>
        <!--</editor-fold>-->
        <v-divider />
        <!--<editor-fold desc="Dialog actions">-->
        <v-card-actions
          class="pa-4 shrink"
        >
          <v-spacer />
          <v-btn
            color="grey"
            text
            @click="customPartDialogVisibleDirty = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="submitCustomPart()"
          >
            <v-icon left>
              check
            </v-icon>
            Submit
          </v-btn>
        </v-card-actions>
        <!--</editor-fold>-->
      </v-card>
    </v-dialog>

    <!--<editor-fold desc="No items">-->
    <v-card
      v-if="!valueDirty || valueDirty.length === 0"
      class="v-card--bordered radius-5"
    >
      <v-card-text
        v-if="!canAddFreeParts"
        class="pa-6 text-center grey--text"
      >
        No parts added.
        <a
          @click.prevent="dialogVisibleDirty = true"
        >
          Add parts
        </a>
      </v-card-text>
      <v-card-text
        v-else
        class="pa-6 text-center grey--text"
      >
        No parts added.
        <a
          @click.prevent="dialogVisibleDirty = true"
        >
          Add parts from inventory
        </a>
        or
        <a
          @click.prevent="addNewCustomPart()"
        >
          Add custom parts
        </a>
      </v-card-text>
    </v-card>
    <!--</editor-fold>-->
    <!--<editor-fold desc="Inventory Items List">-->
    <v-card
      v-else
      class="v-card--bordered radius-5 pa-4"
    >
      <v-row
        v-for="(item, i) in valueDirty"
        :key="i"
        no-gutters
        class="list--bordered py-1"
      >
        <v-col
          cols="auto"
          class="pr-2"
        >
          <v-icon
            color="red"
            @click="removeTaskInventoryItem(item)"
          >
            close
          </v-icon>
        </v-col>
        <v-col
          v-if="item.custom"
          cols="auto"
          class="pr-2"
          style="min-width: 40px"
        >
          <v-icon
            color="primary"
            @click="editCustomPart(item, i)"
          >
            edit
          </v-icon>
        </v-col>
        <v-col>
          <div>
            {{ item.label }}
          </div>
          <div class="caption grey--text">
            <span>{{ item.code }}</span>
            <span v-if="item.custom">
              &middot; {{ item.unit_price | currency(item.currency, 2) }} / {{ item.unit }}
            </span>
          </div>
        </v-col>
        <v-col
          cols="auto"
          class="text-no-wrap"
        >
          {{ item.quantity }} {{ item.unit }}
        </v-col>
      </v-row>
    </v-card>
    <!--</editor-fold>-->
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import InventoryItem from '@/models/InventoryItem'
import serviceCurrentLocationMixin from '@/mixins/serviceCurrentLocationMixin'
import { debounce } from 'lodash'
import CurrencyInput from '@/components/helpers/CurrencyInput'
import CurrencyExchangeRate from '@/models/CurrencyExchangeRate'

const emptyCustomPart = {
  index: undefined,
  code: null,
  label: null,
  unit_price: null,
  currency: 'EUR',
  unit: null,
  quantity: null
}

export default {
  name: 'InventoryItemModalSelect',
  components: { CurrencyInput },
  mixins: [serviceCurrentLocationMixin],
  props: {
    value: {
      type: Array,
      default: () => []
    },
    canAddFreeParts: {
      type: Boolean,
      default: false
    },
    currentEmployeeId: {
      type: Number,
      default: null
    },
    dialogTitle: {
      type: String,
      default: 'Select item'
    },
    dialogVisible: {
      type: Boolean,
      default: false
    },
    customPartDialogVisible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      inventoryItems: {
        loading: false,
        search: '',
        data: []
      },
      selectedInventoryItems: [],
      customPart: emptyCustomPart
    }
  },
  computed: {
    valueDirty: {
      get: function () {
        return this.value
      },
      set: function (val) {
        this.$emit('input', val)
      }
    },
    dialogVisibleDirty: {
      get: function () {
        return this.dialogVisible
      },
      set: function (val) {
        this.$emit('update:dialogVisible', val)
      }
    },
    customPartDialogVisibleDirty: {
      get: function () {
        return this.customPartDialogVisible
      },
      set: function (val) {
        this.$emit('update:customPartDialogVisible', val)
      }
    }
  },
  watch: {
    'inventoryItemsIssued': function () {
      this.inventoryItemsIssuedDirty = cloneDeep(this.inventoryItemsIssued)
    },
    dialogVisible(value) {
      if (!value) return

      this.loadServiceTaskItems()

      setTimeout(() => {
        this.$refs.virtualScroll.onScroll()
      }, 100)
    }
  },
  created() {
    this.currencies = CurrencyExchangeRate.currencies
  },
  mounted() {
    this.inventoryItemsIssuedDirty = cloneDeep(this.inventoryItemsIssued)
  },
  methods: {
    fetchInventoryItems: debounce(async function () {
      this.inventoryItems.loading = true

      try {
        this.inventoryItems.data = await InventoryItem.api.fetch({
          ...this.currentLocationQueryParams,
          search: this.inventoryItems.search
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          'text': 'Something went wrong',
          'color': 'error'
        })
        throw e
      } finally {
        this.inventoryItems.loading = false
        this.$refs.virtualScroll.onScroll()
      }
    }, 500),
    loadServiceTaskItems() {
      this.selectedInventoryItems = []

      this.selectedInventoryItems = this.value.filter(item => !item.custom).map(item => {
        return {
          id: item.id,
          label: item.label,
          unit: item.unit,
          description: item.description,
          quantity: item.quantity,
          code: item.code
        }
      })
    },
    addInventoryItem(item) {
      const currentItemIndex = this.selectedInventoryItems.findIndex(i => i.id === item.id)

      if (currentItemIndex > -1) return

      this.selectedInventoryItems.push({
        id: item.id,
        label: item.label,
        unit: item.unit,
        quantity: null,
        description: item.description,
        code: item.code
      })
    },
    removeInventoryItem(item) {
      const index = this.selectedInventoryItems.findIndex(i => i.id === item.id)

      if (index === -1) return

      this.selectedInventoryItems.splice(index, 1)
    },
    removeTaskInventoryItem(item) {
      const index = this.valueDirty.findIndex(i => i.id === item.id)

      if (index === -1) return

      this.valueDirty.splice(index, 1)
    },
    async submit() {
      const validation = await this.$refs.validationObserver.validate()

      if (!validation) return

      const customItems = this.valueDirty.filter(i => !!i.custom).map(i => ({ ...i }))

      this.valueDirty = [...cloneDeep(this.selectedInventoryItems), ...customItems]
      this.dialogVisibleDirty = false
    },
    addNewCustomPart() {
      this.customPart = cloneDeep(emptyCustomPart)
      this.customPartDialogVisibleDirty = true
    },
    async submitCustomPart() {
      const validation = await this.$refs.customPartValidationObserver.validate()

      if (!validation) return

      const index = this.customPart.index

      if (index !== undefined) {
        this.valueDirty.splice(index, 1, {
          custom: true,
          label: this.customPart.label,
          code: this.customPart.code,
          unit_price: this.customPart.unit_price,
          currency: this.customPart.currency,
          unit: this.customPart.unit,
          quantity: this.customPart.quantity
        })
      } else {
        this.valueDirty.push({
          custom: true,
          label: this.customPart.label,
          code: this.customPart.code,
          unit_price: this.customPart.unit_price,
          currency: this.customPart.currency,
          unit: this.customPart.unit,
          quantity: this.customPart.quantity
        })
      }

      this.customPartDialogVisibleDirty = false
    },
    editCustomPart(item, index) {
      this.customPart = {
        index: index,
        label: item.label,
        code: item.code,
        unit_price: item.unit_price,
        currency: item.currency,
        unit: item.unit,
        quantity: item.quantity
      }

      this.customPartDialogVisibleDirty = true
    }
  }
}
</script>
